/**
 * Classes that keep a surface above the on-screen keyboard on phones and tablets. They read the
 * CSS variables `KeyboardAwareLayout` publishes on the document root (see `useVisualViewport`).
 * Where those aren't set (a desktop, a mouse, no visualViewport), each falls back to what the
 * surface used before: the bottom of the screen and the full screen height.
 *
 * Why arbitrary values (design system section 3, rule 1): they read runtime CSS variables, and
 * they have to be arbitrary (not named Tailwind theme entries) so @kalos/ui's `cn()` (tailwind-merge)
 * recognises them and replaces the primitives' own `bottom-0`, `top-[50%]` and `max-h-svh`;
 * a named entry it doesn't know would leave both on the element, and the stylesheet's order would
 * decide which applies. Write them here once rather than at each call site.
 */

/** For anything fixed to the bottom of the screen (a bottom sheet, a bar of actions): sits on top of the keyboard. */
export const aboveKeyboardClass = 'bottom-[var(--keyboard-inset,0px)]';

/**
 * SheetContent classes for a bottom sheet: on top of the keyboard, and never taller than what the
 * keyboard leaves visible, with a strip of the page showing above it.
 */
export const bottomSheetAboveKeyboardClass =
  'bottom-[var(--keyboard-inset,0px)] max-h-[calc(var(--visual-viewport-height,100dvh)-3rem)]';

/**
 * SheetContent `bodyClassName` for a bottom sheet: its body scrolls up and down only. Content that's
 * too wide is clipped rather than scrolling the sheet sideways, and reaching the end of the body
 * doesn't scroll the page behind it.
 */
export const bottomSheetBodyClass = 'min-w-0 overflow-x-hidden overscroll-contain';

/**
 * For a surface that covers the whole screen (a full-screen dialog, a record's page): exactly the
 * part of the screen the keyboard leaves visible, so its scrolling body and any bar at its bottom
 * end at the top of the keyboard. Combine with `fixed` and `inset-x-0` (or `left-0 w-screen`).
 */
export const fillVisibleScreenClass =
  'top-[var(--visual-viewport-offset-top,0px)] h-[var(--visual-viewport-height,100svh)] max-h-[var(--visual-viewport-height,100svh)]';
